import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Eye, CheckCheck, X, Mail } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { timeLabel } from '../../lib/dateBuckets';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ICONS = { share_viewed: Eye, email: Mail };

export const AdminNotificationsBell = ({ onSelectTab, collapsed }) => {
  const { token } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seen = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/admin/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setData(d);
      const fresh = d.items.filter((n) => !n.read && seen.current && !seen.current.has(n.id));
      fresh.slice(0, 3).forEach((n) => toast(n.title, { description: n.body, icon: <Bell className="w-4 h-4 text-amber-600" /> }));
      seen.current = new Set(d.items.map((n) => n.id));
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, [load]);

  const markAll = async () => { await axios.put(`${API_URL}/admin/notifications/read-all`, {}, { headers }); load(); };
  const openItem = async (n) => {
    if (!n.read) await axios.put(`${API_URL}/admin/notifications/${n.id}/read`, {}, { headers });
    setOpen(false);
    if (n.tab) onSelectTab?.(n.tab);
    load();
  };
  const remove = async (e, n) => { e.stopPropagation(); await axios.delete(`${API_URL}/admin/notifications/${n.id}`, { headers }); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="relative p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900" title="Notifications" data-testid="notifications-bell">
          <Bell className="w-4 h-4" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notifications-badge">{data.unread > 99 ? '99+' : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align={collapsed ? 'start' : 'end'} side="right" className="w-80 p-0 z-[70]" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100">
          <p className="text-xs font-semibold">Notifications {data.unread > 0 && <span className="text-neutral-400 font-normal">· {data.unread} new</span>}</p>
          {data.unread > 0 && <button type="button" onClick={markAll} className="text-[11px] text-amber-700 hover:underline inline-flex items-center gap-1" data-testid="notifications-read-all"><CheckCheck className="w-3 h-3" />Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="px-3 py-8 text-center text-xs text-neutral-500">You're all caught up. We'll tell you when a customer opens a shared preview.</p>}
          {data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} type="button" onClick={() => openItem(n)} className={`w-full text-left px-3 py-2 flex gap-2.5 border-b border-neutral-50 hover:bg-amber-50 ${n.read ? '' : 'bg-amber-50/60'}`} data-testid={`notification-${n.id}`}>
                <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${n.read ? 'bg-neutral-100 text-neutral-500' : 'bg-amber-100 text-amber-700'}`}><Icon className="w-3.5 h-3.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-neutral-900 leading-snug">{n.title}</span>
                  {n.body && <span className="block text-[11px] text-neutral-500 truncate">{n.body}</span>}
                  <span className="block text-[10px] text-neutral-400 mt-0.5">{timeLabel(n.created_at)}</span>
                </span>
                <span role="button" onClick={(e) => remove(e, n)} className="p-1 rounded text-neutral-300 hover:text-neutral-600 hover:bg-white self-start" title="Dismiss" data-testid={`notification-dismiss-${n.id}`}><X className="w-3 h-3" /></span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AdminNotificationsBell;
